/**
 * App-message slice (SCRUM-1950, RTK bundle 1 of 5): createSlice migration of
 * the legacy `appMessageReducer` switch.
 *
 * Transient app-level notification, kept off the errors slice so a success/info
 * signal never reaches the error-only path (SCRUM-882). As of SCRUM-883 it
 * carries the ready-to-show payload `{ type, message }` (no sentinel round-trip):
 * a thunk publishes the final text + kind, and the global notification hub
 * (`GlobalNotifications`) is the sole consumer — it toasts `message` as `type`
 * and clears the slice.
 *
 * The legacy strings from `appMessageTypes.js` are kept; `appMessageActions.js`
 * is unchanged and `appMessageReducer.js` re-exports this reducer. Pinned by
 * src/tests/unit/rtkAppShell.characterisation.test.js.
 */
import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { SET_APP_MESSAGE, CLEAR_APP_MESSAGE } from "./appMessageTypes";

type AppMessage = { type: "success" | "info"; message: string };

/** The slice holds the message to show, or null when there is none. */
type AppMessageState = AppMessage | null;

const initialState = null as AppMessageState;

const appMessageSlice = createSlice({
  name: "appMessage",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(SET_APP_MESSAGE, (_state, action: PayloadAction<AppMessage, typeof SET_APP_MESSAGE>) => action.payload)
      .addCase(CLEAR_APP_MESSAGE, () => null);
  },
});

export default appMessageSlice.reducer;
